// 📁 src/pages/admin/TenantMatrixPage.jsx

import React, { useState, useEffect } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/admin.css';
import { exportToExcel, exportToWord, printFiltered } from '../../utils/exportUtils';

export default function TenantMatrixPage() {
  const [tenants, setTenants] = useState([]);
  const [expandedTenantId, setExpandedTenantId] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    payment: '',
    module: ''
  });
  const [visibleCount, setVisibleCount] = useState(10);

  const hydrateTenants = () => {
    const accountRegistry = JSON.parse(localStorage.getItem('accountRegistry') || '{}');
    const allKeys = Object.keys(localStorage).filter((key) => key.startsWith('paymentAuditLog_'));

    const tenantMap = new Map();

    allKeys.forEach((key) => {
      const email = key.replace('paymentAuditLog_', '');
      const auditLog = JSON.parse(localStorage.getItem(key) || '[]');
      const account = accountRegistry[email] || {};
      const companyName = account.companyName || 'Unknown Company';

      auditLog.forEach((entry) => {
        const { tenantId, moduleName, paymentStatus, purchaseDate, status } = entry;
        if (!tenantId || !moduleName) return;

        const normalizedId = tenantId.trim().toLowerCase();
        const paidMonth = paymentStatus === 'paid' ? purchaseDate : null;
        const unpaidMonth = paymentStatus === 'unpaid' ? purchaseDate : null;

        const key = `${email}::${normalizedId}`;
        if (!tenantMap.has(key)) {
          tenantMap.set(key, {
            email,
            companyName,
            tenantId,
            modules: []
          });
        }

        const tenant = tenantMap.get(key);
        const moduleExists = tenant.modules.some((m) => m.name === moduleName);
        if (!moduleExists) {
          tenant.modules.push({
            name: moduleName,
            paymentStatus,
            paidMonth,
            unpaidMonth,
            status: status || (paymentStatus === 'paid' ? 'active' : 'inactive') // ✅ restored for display
          });
        }
      });
    });

    const sortedTenants = Array.from(tenantMap.values());
    setTenants(sortedTenants);
  };

  useEffect(() => {
    hydrateTenants();

    const handleStorageChange = () => {
      hydrateTenants();
    };

    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(hydrateTenants, 30000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const toggleExpand = (tenantId) => {
    setExpandedTenantId((prev) => (prev === tenantId ? null : tenantId));
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setVisibleCount(10);
  };

  const handleAction = (type, tenantId, moduleName = null) => {
    const updated = [...tenants];
    const index = updated.findIndex((t) => t.tenantId === tenantId);
    if (index === -1) return;

    const tenant = updated[index];

    switch (type) {
      case 'payment':
        tenant.modules = tenant.modules.map((mod) =>
          mod.name === moduleName
            ? {
                ...mod,
                paymentStatus: 'paid',
                paidMonth: new Date().toLocaleString('default', {
                  month: 'short',
                  year: 'numeric'
                }),
                unpaidMonth: null,
                status: 'active'
              }
            : mod
        );
        break;

      case 'export':
        const moduleData = tenant.modules.find((m) => m.name === moduleName);
        exportToExcel([{ ...tenant, modules: [moduleData] }]);
        return;

      case 'integrate':
        tenant.modules = tenant.modules.map((mod) => ({
          ...mod,
          integrationStatus: 'linked'
        }));
        break;

      default:
        console.warn('Unknown action type:', type);
    }

    tenant.lastAction = new Date().toISOString();
    setTenants(updated);
    localStorage.setItem('tenants', JSON.stringify(updated));
  };

  const filteredTenants = tenants.filter((t) => {
    const matchesSearch = filters.search
      ? (t.companyName || '').toLowerCase().includes(filters.search.toLowerCase()) ||
        (t.tenantId || '').toLowerCase().includes(filters.search.toLowerCase())
      : true;

    const matchesModule = filters.module
      ? t.modules.some((m) => m.name.toLowerCase().includes(filters.module.toLowerCase()))
      : true;

    const matchesStatus = filters.status
      ? t.modules.some((m) => m.status?.toLowerCase() === filters.status.toLowerCase())
      : true;

    const matchesPayment = filters.payment
      ? filters.payment === 'unpaid'
        ? t.modules.some((m) => m.paymentStatus.toLowerCase() === 'unpaid')
        : filters.payment === 'paid'
          ? t.modules.some((m) => m.paymentStatus.toLowerCase() === 'paid')
          : true
      : true;

    return matchesSearch && matchesModule && matchesStatus && matchesPayment;
  });

  const visibleTenants = filteredTenants.slice(0, visibleCount);

  const TenantRow = ({ tenant, isExpanded, onToggle, handleAction }) => (
    <div className="border rounded bg-white shadow-sm cursor-pointer" onClick={onToggle}>
      <div className="flex justify-between items-center px-4 py-2">
        <div>
          <div className="font-semibold">{tenant.companyName}</div>
          <div className="text-xs text-gray-500">{tenant.tenantId}</div>
        </div>
        <div className="text-sm text-gray-600 italic">
          {tenant.modules.map((mod) => (
            <span key={mod.name} className="mr-3">
              {mod.name} ({mod.paidMonth || mod.unpaidMonth}) — {mod.paymentStatus}, {mod.status}
            </span>
          ))}
        </div>
      </div>

      {isExpanded && (
        <div className="bg-gray-50 border-t px-4 py-3 space-y-3">
          <div className="flex justify-end">
            <button
              className="bg-orange-500 text-white px-3 py-1 rounded text-sm"
              onClick={(e) => {
                e.stopPropagation();
                handleAction('integrate', tenant.tenantId);
              }}
            >
              🔗 Integrate Modules
            </button>
          </div>

          {tenant.modules.map((mod, index) => (
            <div key={index} className="flex justify-between items-center border-b pb-2">
              <div>
                <div className="text-sm font-medium">{mod.name}</div>
                <div className="text-xs text-gray-500">
                  Payment: {mod.paymentStatus}
                  {mod.unpaidMonth
                    ? ` (${mod.unpaidMonth})`
                    : mod.paidMonth
                      ? ` (${mod.paidMonth})`
                      : ''}
                  {' | '}Status: {mod.status}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  className="bg-indigo-600 text-white px-2 py-1 rounded text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction('payment', tenant.tenantId, mod.name);
                  }}
                >
                  Trigger Payment
                </button>
                <button
                  className="bg-green-600 text-white px-2 py-1 rounded text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction('export', tenant.tenantId, mod.name);
                  }}
                >
                  Print Report
                </button>
              </div>
            </div>
          ))}

          <div className="text-xs text-gray-500 mt-2">
            Last action: {tenant.lastAction ? new Date(tenant.lastAction).toLocaleString() : '—'}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4">🏢 Tenant Control Matrix</h2>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
        <input
          type="text"
          name="search"
          value={filters.search}
          onChange={handleFilterChange}
          placeholder="Search by company or ID"
          className="border px-2 py-1 rounded text-sm"
        />
        <input
          type="text"
          name="module"
          value={filters.module}
          onChange={handleFilterChange}
          placeholder="Filter by module"
          className="border px-2 py-1 rounded text-sm"
        />
        <select
          name="status"
          value={filters.status}
          onChange={handleFilterChange}
          className="border px-2 py-1 rounded text-sm"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          name="payment"
          value={filters.payment}
          onChange={handleFilterChange}
          className="border px-2 py-1 rounded text-sm"
        >
          <option value="">All Payments</option>
          <option value="paid">Paid</option>
          <option value="unpaid">Unpaid</option>
        </select>
      </div>

      <div className="flex gap-2 justify-end mb-6">
        <button
          className="bg-green-600 text-white px-3 py-1 rounded text-sm"
          onClick={() => printFiltered(filteredTenants)}
        >
          🖨 Print Report
        </button>
        <button
          className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
          onClick={() => exportToWord(filteredTenants)}
        >
          📁 Export Report
        </button>
      </div>

      {visibleTenants.length === 0 && (
        <div className="text-center text-red-600 font-semibold mt-4">
          No tenants found. Check your filters.
        </div>
      )}

      <div className="space-y-4">
        {visibleTenants.map((tenant) => (
          <TenantRow
            key={`${tenant.email}::${tenant.tenantId}`}
            tenant={tenant}
            isExpanded={expandedTenantId === tenant.tenantId}
            onToggle={() => toggleExpand(tenant.tenantId)}
            handleAction={handleAction}
          />
        ))}
      </div>
    </div>
  );
}
